import React, { useEffect } from "react";
import Collapsible from "react-collapsible";
import { IoCloseCircle } from "react-icons/io5";
import { SideBarPortalDataTable } from "./PortalDataTable";
import {
  formActionHistoryColumn,
  formCommentColumn,
  formTransitHistoryColumn,
} from "../utils";

/**
 * Responsive panel:
 * - Mobile (default): bottom sheet (h-[85vh]), full-width, rounded top, backdrop overlay.
 * - md+ : right-side drawer (fixed, h-screen, 600px), no dark backdrop.
 * - Tables are wrapped in overflow-x-auto to avoid horizontal scrolling issues.
 * - Hidden on print.
 */
const FormMetadata = ({
  togglePanel,
  formHistoryActions = [],
  formTransitHistories = [],
  comments = [],
}) => {
  // ESC to close (nice UX)
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && togglePanel?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [togglePanel]);

  return (
    <div className="fixed inset-0 z-40 print:hidden">
      {/* Backdrop: visible on mobile, transparent on md+ */}
      <div
        className="absolute inset-0 bg-black/30 md:bg-transparent"
        onClick={togglePanel}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        id="formMetadata"
        role="dialog"
        aria-modal="true"
        className={[
          // mobile bottom sheet
          "absolute bottom-0 left-0 right-0 h-[85vh] w-full rounded-t-2xl bg-white shadow-lg",
          // md+ right drawer
          "md:top-[70px] md:right-0 md:left-auto md:bottom-auto md:h-screen md:w-[600px] md:rounded-none md:border-l",
          "border border-gray-200 overflow-y-auto p-4 sm:p-5",
          // avoid layout shift
          "transition-transform duration-200 ease-out",
        ].join(" ")}
      >
        {/* Drag handle on mobile */}
        <div className="md:hidden flex justify-center">
          <div className="w-12 h-1.5 rounded-full bg-gray-300 mb-2" />
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={togglePanel}
          className="ml-auto mb-3 flex items-center text-red-600 hover:scale-105 transition-transform"
          aria-label="Close details panel"
          title="Close"
        >
          <IoCloseCircle className="text-3xl" />
        </button>

        {/* Action History */}
        <Collapsible
          open={false}
          trigger={
            <div className="w-full p-3 flex justify-between items-center">
              <span className="text-base sm:text-lg">Action History</span>
            </div>
          }
          triggerClassName="font-medium text-lg sm:text-xl"
          triggerOpenedClassName="font-medium text-lg sm:text-xl w-full"
          className="mb-4 border w-full rounded"
          openedClassName="border w-full mb-6 rounded"
          contentInnerClassName="p-4 mt-3 border-t w-full"
        >
          <div className="border rounded overflow-x-auto">
            <SideBarPortalDataTable
              cols={formActionHistoryColumn}
              loading={false}
              rows={formHistoryActions}
            />
          </div>
        </Collapsible>

        {/* Transit History */}
        <Collapsible
          open={false}
          trigger={
            <div className="w-full p-3 flex justify-between items-center">
              <span className="text-base sm:text-lg">Transit History</span>
            </div>
          }
          triggerClassName="font-medium text-lg sm:text-xl"
          triggerOpenedClassName="font-medium text-lg sm:text-xl w-full"
          className="mb-4 border w-full rounded"
          openedClassName="border w-full mb-6 rounded"
          contentInnerClassName="p-4 mt-3 border-t w-full"
        >
          <div className="border rounded overflow-x-auto">
            <SideBarPortalDataTable
              cols={formTransitHistoryColumn}
              loading={false}
              rows={formTransitHistories}
            />
          </div>
        </Collapsible>

        {/* Comments */}
        <Collapsible
          open={false}
          trigger={
            <div className="w-full p-3 flex justify-between items-center">
              <span className="text-base sm:text-lg">Comments</span>
            </div>
          }
          triggerClassName="font-medium text-lg sm:text-xl"
          triggerOpenedClassName="font-medium text-lg sm:text-xl w-full"
          className="mb-4 border w-full rounded"
          openedClassName="border w-full mb-6 rounded"
          contentInnerClassName="p-4 mt-3 border-t w-full"
        >
          <div className="border rounded overflow-x-auto">
            <SideBarPortalDataTable
              cols={formCommentColumn}
              loading={false}
              rows={comments}
            />
          </div>
        </Collapsible>
      </div>
    </div>
  );
};

export default FormMetadata;
